import { normalizeUrlPath } from "./policies";
import {
  getOfflineEntityId,
  getOfflineUserId,
} from "./offlineReferences";

const LIST_RESOURCES = new Set([
  "students",
  "staffs",
  "sections",
  "sectionclasses",
  "years",
  "leaves",
  "presences",
  "daystaffs",
  "parentfamilies",
  "suppliers",
  "territories",
  "communes",
  "groupments",
  "quartiers",
  "chartaccounts",
  "expenses",
  "expensedetails",
  "gifts",
  "schoolfees",
  "schoolfeecategories",
  "sectionschoolfeecategories",
  "estsectschoolcatstudcats",
  "transports",
  "typesalaries",
]);

const parseData = (data) => {
  if (typeof data !== "string") return data || {};
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const deepMerge = (current, changes) => {
  if (
    !current ||
    !changes ||
    Array.isArray(current) ||
    Array.isArray(changes) ||
    typeof current !== "object" ||
    typeof changes !== "object"
  ) {
    return changes;
  }

  return Object.keys(changes).reduce(
    (result, key) => ({
      ...result,
      [key]: deepMerge(current[key], changes[key]),
    }),
    { ...current }
  );
};

const normalizePayload = (resource, rawData) => {
  const data = parseData(rawData);
  if (resource === "students") {
    return { ...(data.student || {}), user: data.user || undefined };
  }
  if (resource === "staffs") {
    return { ...(data.staff || {}), user: data.user || undefined };
  }
  return data;
};

const normalizeOptimisticCreation = (resource, changes, queued) => {
  if (resource !== "parentfamilies") return changes;

  const userId = changes.userId || getOfflineUserId(queued);
  return {
    ...changes,
    userId,
    user: {
      ...changes,
      ...(changes.user || {}),
      id: changes.user?.id || userId,
    },
  };
};

const indexById = (items = []) =>
  new Map(
    (Array.isArray(items) ? items : [])
      .filter((item) => item?.id !== undefined && item?.id !== null)
      .map((item) => [String(item.id), item])
  );

const relation = (indexes, resource, id) =>
  id === undefined || id === null || id === ""
    ? undefined
    : indexes[resource]?.get(String(id));

const hydrateRelations = (resource, item, indexes) => {
  if (resource === "students") {
    return {
      ...item,
      sectionclass:
        item.sectionclass ||
        relation(indexes, "sectionclasses", item.sectionclasseId),
      year: item.year || relation(indexes, "years", item.yearId),
      commune: item.commune || relation(indexes, "communes", item.communeId),
      territory:
        item.territory || relation(indexes, "territories", item.territoryId),
      groupment:
        item.groupment || relation(indexes, "groupments", item.groupmentId),
      quartier:
        item.quartier || relation(indexes, "quartiers", item.quartierId),
    };
  }
  if (resource === "sections") {
    return {
      ...item,
      year: item.year || relation(indexes, "years", item.yearId),
    };
  }
  if (resource === "sectionclasses") {
    return {
      ...item,
      class:
        item.class ||
        relation(indexes, "classes", item.classeId || item.classId),
      section:
        item.section || relation(indexes, "sections", item.sectionId),
      reportcardcategory:
        item.reportcardcategory ||
        relation(indexes, "reportcardcategories", item.reportcardcategoryId),
      year: item.year || relation(indexes, "years", item.yearId),
    };
  }
  if (resource === "leaves") {
    return {
      ...item,
      staff: item.staff || relation(indexes, "staffs", item.staffId),
    };
  }
  if (resource === "presences") {
    return {
      ...item,
      staff: item.staff || relation(indexes, "staffs", item.staffId),
      student:
        item.student || relation(indexes, "students", item.studentId),
    };
  }
  if (resource === "daystaffs") {
    return {
      ...item,
      day: item.day || relation(indexes, "days", item.dayId),
      staff: item.staff || relation(indexes, "staffs", item.staffId),
      year: item.year || relation(indexes, "years", item.yearId),
    };
  }
  return item;
};

const mutationTarget = (queued) => {
  const parts = normalizeUrlPath(queued.url).split("/").filter(Boolean);
  if (!LIST_RESOURCES.has(parts[0]) || parts.length > 2) return null;
  return { resource: parts[0], id: parts[1] };
};

export const getListResource = (config) => {
  const parts = normalizeUrlPath(config?.url).split("/").filter(Boolean);
  return parts.length === 1 && LIST_RESOURCES.has(parts[0]) ? parts[0] : null;
};

export const createOptimisticQueuedItem = (queued, references = {}) => {
  const target = mutationTarget(queued);
  if (!target || queued.method !== "post") return null;

  const changes = normalizeOptimisticCreation(
    target.resource,
    normalizePayload(target.resource, queued.data),
    queued
  );
  const indexes = Object.keys(references).reduce(
    (result, key) => ({ ...result, [key]: indexById(references[key]) }),
    {}
  );

  return hydrateRelations(
    target.resource,
    {
      ...changes,
      id: getOfflineEntityId(queued),
      createdAt:
        changes.createdAt || new Date(queued.createdAt).toISOString(),
      _offlinePending: true,
      _offline: {
        pending: true,
        queueId: queued.id,
        status: queued.status,
        error: queued.error || null,
        createdAt: queued.createdAt,
      },
    },
    indexes
  );
};

export const projectQueuedMutations = (
  config,
  data,
  queueItems = [],
  references = {}
) => {
  const resource = getListResource(config);
  if (!resource || !Array.isArray(data)) return data;

  const indexes = Object.keys(references).reduce(
    (result, key) => ({ ...result, [key]: indexById(references[key]) }),
    {}
  );
  indexes[resource] = indexById(data);

  return queueItems.reduce((items, queued) => {
    const target = mutationTarget(queued);
    if (!target || target.resource !== resource) return items;

    const changes = normalizePayload(resource, queued.data);
    const offlineState = {
      pending: true,
      queueId: queued.id,
      status: queued.status,
      error: queued.error || null,
      createdAt: queued.createdAt,
    };

    if (queued.method === "post") {
      const optimisticItem = createOptimisticQueuedItem(queued, references);
      const id = optimisticItem.id;
      return items.some((item) => String(item.id) === String(id))
        ? items
        : [...items, optimisticItem];
    }

    if (["put", "patch"].includes(queued.method) && target.id) {
      return items.map((item) =>
        String(item.id) === String(target.id)
          ? hydrateRelations(
              resource,
              {
                ...deepMerge(item, changes),
                _offlinePending: true,
                _offline: offlineState,
              },
              indexes
            )
          : item
      );
    }
    return items;
  }, data.map((item) => ({ ...item })));
};

export const OPTIMISTIC_REFERENCE_RESOURCES = [
  "years",
  "sections",
  "sectionclasses",
  "classes",
  "reportcardcategories",
  "staffs",
  "students",
  "days",
  "territories",
  "communes",
  "groupments",
  "quartiers",
  "months",
  "trimesters",
  "suppliers",
  "chartaccounts",
  "schoolfeecategories",
  "transports",
  "typesalaries",
  "users",
];
